"use strict";
/**
 * Pad 2, layout E+M, at /app/win-prob/nba/<gameId>/tap?home=…&away=…&pad=2 (ai/nba/N8-taps/PLAN.md
 * §19.5, §19a). Pad 1's handler is untouched; this one reuses its queue, session and clock-sync
 * rule, tracking, FT areas and swap gate, status line and parking, and adds the fold of the game's
 * record, the two-step shot, the tip question, PULL's latch and the holds.
 *
 * **The fold** (§19a.4, §19e): until the record is complete, the phone's stored taps for the game
 * and this pad's own (`nbaLoadingTaps`); then `nbaReloadTaps` (record, acknowledged, unsent; parked
 * local copies out).
 *
 * **Shot age** (§19.5): the shot that set the fold's context (its side's latest current shot not
 * undone by a current UNDO). Every Pad 2 time is on the wall clock its clientOffsetMs is measured
 * against (`tapTime`), so the paint and the tap age a shot alike. This pad's own shot is aged raw;
 * another session's (a reload's) by clientTs + clientOffsetMs against now + the pad's offset, or raw
 * when either offset is unknown, so a reload mid-shot still records the make.
 *
 * **Holds** fire at their completion and are stamped then. Timers and listeners are this pad's
 * own: added on open, removed on close. Nothing here is on the order path.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.EXIT_CLICK_GUARD_MS = exports.nbaV2PeriodTarget = exports.TIP_GATE_MS = exports.RECORD_REFRESH_MS = exports.RECORD_TIMEOUT_MS = void 0;
exports.nbaV2PartialSince = nbaV2PartialSince;
exports.nbaV2UndoTarget = nbaV2UndoTarget;
exports.nbaV2ContextShots = nbaV2ContextShots;
exports.openNbaTapPadV2 = openNbaTapPadV2;
exports.closeNbaTapPadV2 = closeNbaTapPadV2;
const taps_1 = require("../api/taps");
const tap_queue_1 = require("../services/tap-queue");
const nba_record_1 = require("../services/nba-record");
const nba_pad_v2_cues_1 = require("../services/nba-pad-v2-cues");
const uuid_1 = require("../services/uuid");
const hold_gesture_1 = require("./hold-gesture");
const parked_hold_1 = require("./parked-hold");
const nba_tap_handler_1 = require("./nba-tap-handler");
const tap_parked_1 = require("../ui/tap-parked");
const nba_tap_pad_1 = require("../ui/nba-tap-pad");
const nba_tap_pad_v2_1 = require("../ui/nba-tap-pad-v2");
const ft_pending_1 = require("../../shared/ft-pending");
const nba_tap_state_1 = require("../../shared/nba-tap-state");
const tap_vocabulary_1 = require("../../shared/tap-vocabulary");
const FLUSH_MS = 500;
const SYNC_MS = 60000;
const SYNC_SAMPLES = 5;
const REFUSAL_MS = 2500;
const SEND_TIMEOUT_MS = 5000;
const CLOCK_TIMEOUT_MS = 3000;
const PAD_VERSION = 2;
/** A record page is up to 1,000 rows; one that has not answered by then is a failure, retried from the last page. */
exports.RECORD_TIMEOUT_MS = 10000;
/** A re-read of the record while visible, so acknowledgements are confirmed and dropped during the game (one small page a minute). */
exports.RECORD_REFRESH_MS = 60000;
/** The tip prompt ignores presses for this long after it appears: one aimed at what was under it is not an answer. */
exports.TIP_GATE_MS = nba_tap_handler_1.GATE_MS;
const LABELS = {
    shot_2: "2PT", shot_3: "3PT", made_2: "Made 2", made_3: "Made 3", missed: "Missed", ball: "Ball", off_rebound: "Off Reb",
    ft_made: "FT made", ft_missed: "FT miss", ft_trip_1: "FT ×1", ft_trip_2: "FT ×2", ft_trip_3: "FT ×3",
    ft_tech: "Tech FT", ft_tech_made: "Tech made", ft_tech_missed: "Tech missed",
    period_next: "next period", tip_confirm: "tip answer", pull: "PULL", resume: "resume", undo: "UNDO",
};
let pad = null;
const parkedHold = new parked_hold_1.ParkedHold();
const body = () => document.getElementById("win-prob-body");
const isV2Kind = (v) => typeof v === "string" && tap_vocabulary_1.NBA_KINDS_V2.includes(v);
const isSide = (v) => v === "home" || v === "away";
/**
 * The event's wall time: its age on the event clock, back from Date.now(). Not timeOrigin +
 * timeStamp: WebKit's event clock stops while the phone sleeps, so that falls behind Date.now().
 */
const tapTime = (event) => Date.now() - Math.max(0, performance.now() - event.timeStamp);
const ts = (t) => Date.parse(t.clientTs ?? "");
/** period_next's target (§19a.6): the fold's period + 1, or none when the fold does not know it. */
const nbaV2PeriodTarget = (period) => (period === null ? null : period + 1);
exports.nbaV2PeriodTarget = nbaV2PeriodTarget;
/** The partial tally's start: the earliest clientTs in the folded taps. */
function nbaV2PartialSince(taps) {
    let min = Number.POSITIVE_INFINITY;
    for (const t of taps) {
        const at = ts(t);
        if (at < min)
            min = at;
    }
    return Number.isFinite(min) ? min : null;
}
/** Pad 1's UNDO rule (nbaUndoTarget) with reachability by version: this session's latest reachable tap not undone. */
function nbaV2UndoTarget(history, sessionId) {
    const undone = new Set(history.filter((t) => t.kind === "undo").map((t) => t.undoesTapId));
    for (let i = history.length - 1; i >= 0; i--) {
        const t = history[i];
        if (t.sessionId !== sessionId)
            return null;
        if (t.kind !== "undo" && (0, ft_pending_1.nbaUndoReachable)(t) && !undone.has(t.tapId))
            return t;
    }
    return null;
}
/**
 * Each side's latest shot the fold applied to context: current at receipt (STATE-FOLD-V2: the
 * session of the latest session_start so far), not a retry, not the target of a valid current UNDO.
 * When the fold's ctx is that side's shot, this is the shot that set it.
 */
function nbaV2ContextShots(taps) {
    const idOf = (v) => (typeof v === "string" ? v : null);
    const seen = new Map();
    const undone = new Set();
    const skip = new Set();
    const shots = [];
    let session;
    for (const t of taps) {
        const id = idOf(t.tapId);
        if (seen.has(id))
            continue;
        const sid = idOf(t.sessionId);
        let current = true;
        if (t.kind === "session_start")
            session = sid;
        else
            current = session === undefined || sid === session;
        if (t.kind === "undo") {
            const target = seen.get(idOf(t.undoesTapId));
            if (target !== undefined && idOf(target.sessionId) === sid && (0, ft_pending_1.nbaUndoReachable)(target) && !undone.has(idOf(target.tapId))) {
                undone.add(idOf(target.tapId));
                if (current)
                    skip.add(idOf(target.tapId));
            }
        }
        seen.set(id, t);
        if (current && (t.kind === "shot_2" || t.kind === "shot_3") && isSide(t.side))
            shots.push(t);
    }
    const out = { home: null, away: null };
    for (let i = shots.length - 1; i >= 0; i--) {
        const t = shots[i];
        const side = t.side;
        if (out[side] !== null || skip.has(idOf(t.tapId)))
            continue;
        out[side] = { tapId: t.tapId, atMs: ts(t), offsetMs: t.clientOffsetMs ?? null };
    }
    return out;
}
/** Ms since the side's latest shot at `nowMs` (see the header), or null with none. */
function shotAge(p, side, nowMs) {
    const s = p.shots[side];
    if (!s || !Number.isFinite(s.atMs))
        return null;
    if (p.ownIds.has(s.tapId) || s.offsetMs === null || p.clock === null)
        return nowMs - s.atMs;
    return nowMs + p.clock.offsetMs - (s.atMs + s.offsetMs);
}
function refold(p) {
    const parked = p.queue.parkedIds;
    if (parked.size)
        p.own = p.own.filter((t) => !parked.has(t.tapId));
    const record = p.loader.state;
    p.input = record.status === "complete" ? (0, nba_record_1.nbaReloadTaps)(p.queue, record) : (0, nba_record_1.nbaLoadingTaps)(p.queue, p.own);
    p.state = p.input.length ? (0, nba_tap_state_1.nbaStateRefold)(p.input) : null;
    p.shots = nbaV2ContextShots(p.input);
}
function paintFt(p, root) {
    const ft = root.querySelector(".nba-pad-em .nba-ft-state");
    const session = p.own.filter((t) => t.sessionId === p.sessionId).map((t) => ({ ...t, sessionId: t.sessionId ?? null }));
    const steps = (0, ft_pending_1.deriveFtPending)(session);
    const step = steps[steps.length - 1] ?? { trip: { kind: "unknown" }, tech: null };
    if (ft) {
        ft.dataset.ftState = step.trip.kind;
        ft.innerHTML = (0, nba_tap_pad_1.renderNbaFtState)(step, p.teams);
    }
    const areas = (0, nba_tap_pad_1.nbaFtAreas)(step);
    const now = performance.now();
    for (const side of ["away", "home"]) {
        const area = root.querySelector(`.nba-pad-em .nba-ft-area[data-side="${side}"]`);
        if (!area)
            continue;
        const { mode, held, progress } = areas[side];
        if (mode !== p.ftMode[side]) {
            p.ftMode[side] = mode;
            p.swappedAt[side] = now;
            later(p, nba_tap_handler_1.GATE_MS, () => paint(p, false)); // clears the gated look
        }
        area.dataset.mode = mode;
        area.classList.toggle("nba-ft-held", held);
        area.classList.toggle("nba-ft-gated", now - p.swappedAt[side] < nba_tap_handler_1.GATE_MS);
        const prog = area.querySelector(".nba-ft-progress");
        if (prog)
            prog.textContent = progress;
        for (const b of area.querySelectorAll(".nba-ft-swaps"))
            b.disabled = (0, nba_tap_pad_1.nbaFtButtonMode)(b.dataset.tapKind ?? "") !== mode;
    }
}
function paintFoot(p, root) {
    (0, tap_parked_1.showParked)(root.querySelector(".nba-pad-em .tap-parked"), p.queue.parked.length);
    const status = root.querySelector(".nba-pad-em .nba-em-status");
    const refused = p.refusal && p.refusal.until > Date.now() ? p.refusal.text : null;
    if (status) {
        status.classList.toggle("tap-refused", refused !== null ? !p.refusal?.ok : p.queue.storageFailing);
        if (refused !== null)
            status.textContent = refused;
        else {
            const record = p.loader.state;
            const line = (0, nba_tap_pad_1.renderNbaTapStatus)({
                storageFailing: p.queue.storageFailing,
                pending: p.queue.size,
                failedSends: p.queue.failedSends,
                offsetMs: p.clock?.offsetMs ?? null,
                rttMs: p.clock?.rttMs ?? null,
                syncedAgoS: p.clock ? Math.round((Date.now() - p.clock.at) / 1000) : null,
                error: p.queue.lastError,
                conflicts: p.queue.conflicts.length,
                screenAwake: p.wakeLock !== null,
            });
            status.innerHTML = record.status !== "complete" && record.failures > 0 ? `record retrying · ${line}` : line;
        }
    }
    const last = [...p.own].reverse().find((t) => t.kind !== "session_start" && t.kind !== "track_start" && t.kind !== "track_stop");
    const lastEl = root.querySelector(".nba-pad-em .nba-em-last");
    if (lastEl) {
        const text = last
            ? `last ${new Date(ts(last)).toLocaleTimeString(undefined, { hour12: false })} ${LABELS[last.kind] ?? last.kind}${last.side ? ` ${last.side === "home" ? p.teams.home : p.teams.away}` : ""}`
            : "";
        if (lastEl.textContent !== text)
            lastEl.textContent = text;
    }
    const track = (0, nba_tap_pad_1.renderNbaTrack)({
        synced: p.sessionSynced,
        sinceMs: p.trackingSince,
        leftOnSinceMs: (0, nba_tap_pad_1.nbaTrackingLeftOn)(p.input.map((t) => ({ tapId: t.tapId, kind: t.kind, side: t.side, undoesTapId: null, atMs: ts(t), sessionId: t.sessionId ?? null }))),
    });
    (0, nba_tap_pad_v2_1.paintNbaPadV2Track)(root, { ...track, label: track.on ? track.label.replace(/Stop$/, "hold to stop") : track.label });
}
/** Repaints from the fold; `refoldFirst` false (the 1 s tick) only re-reads the shots' ages. */
function paint(p, refoldFirst = true) {
    if (pad !== p)
        return;
    if (refoldFirst)
        refold(p);
    const now = Date.now();
    const view = (0, nba_pad_v2_cues_1.nbaPadV2View)({
        state: p.state,
        taps: p.input,
        shotAgeMs: { home: shotAge(p, "home", now), away: shotAge(p, "away", now) },
        recordComplete: p.loader.state.status === "complete",
        espnPreselect: p.espnPreselect,
        partialSinceMs: nbaV2PartialSince(p.input),
    });
    if (view.tipPrompt === null)
        p.tipShownAt = null;
    else
        p.tipShownAt ?? (p.tipShownAt = performance.now());
    const root = body();
    if (!root)
        return;
    (0, nba_tap_pad_v2_1.paintNbaPadV2)(root, view);
    paintFt(p, root);
    paintFoot(p, root);
}
function later(p, ms, fn) {
    const id = window.setTimeout(() => {
        p.timers.delete(id);
        fn();
    }, ms);
    p.timers.add(id);
}
function refuse(p, text) {
    p.refusal = { text, until: Date.now() + REFUSAL_MS };
    navigator.vibrate?.([40, 40, 40]);
    paint(p, false);
}
function flush(p) {
    void p.queue.flush().then(() => {
        if (pad === p)
            paint(p);
    });
}
/** Builds, queues and sends one version 2 tap; the send starts before anything is painted. */
function send(p, kind, side, atMs, extra = {}) {
    const base = (0, nba_tap_pad_1.buildNbaTap)({
        tapId: (0, uuid_1.uuidV4)(),
        kind: kind, // v2 kinds; sidedness is set here
        side: tap_vocabulary_1.NBA_UNSIDED_TAP_KINDS.has(kind) ? null : side,
        atMs,
        nowMs: Date.now(),
        clock: p.clock,
        undoesTapId: null,
        sessionId: p.sessionId,
    });
    const tap = { ...base, padVersion: PAD_VERSION, ...extra };
    p.queue.add(tap);
    flush(p);
    p.own.push(tap);
    p.ownIds.add(tap.tapId);
    return tap;
}
function startSession(p) {
    p.sessionId = (0, uuid_1.uuidV4)();
    p.sessionSynced = p.clock !== null;
    send(p, "session_start", null, Date.now());
}
function withDeadline(promise, ms) {
    let timer;
    const expired = new Promise((_, reject) => {
        timer = window.setTimeout(() => reject(new Error(`no answer in ${ms} ms`)), ms);
    });
    return Promise.race([promise, expired]).finally(() => window.clearTimeout(timer));
}
/** Pad 1's sync: a newer sync supersedes this one; the first offset starts a synchronized session. */
async function syncClock(p) {
    const run = ++p.syncRun;
    const samples = [];
    for (let i = 0; i < SYNC_SAMPLES; i++) {
        try {
            const t0 = Date.now();
            const serverMs = await withDeadline((0, taps_1.fetchServerClock)(), CLOCK_TIMEOUT_MS);
            samples.push({ t0, t1: Date.now(), serverMs });
        }
        catch {
            break;
        }
        if (p.syncRun !== run)
            return;
    }
    if (p.syncRun !== run || pad !== p)
        return;
    const best = (0, tap_queue_1.estimateClockSample)(samples);
    if (best) {
        p.clock = best;
        if (!p.sessionSynced)
            startSession(p);
    }
    paint(p);
}
async function holdScreen(p) {
    if (p.wakeLock || p.wakeLockAsking || document.visibilityState !== "visible" || !navigator.wakeLock)
        return;
    p.wakeLockAsking = true;
    try {
        const lock = await navigator.wakeLock.request("screen");
        if (pad !== p) {
            void lock.release();
            return;
        }
        p.wakeLock = lock;
        lock.addEventListener("release", () => {
            if (p.wakeLock === lock)
                p.wakeLock = null;
            if (pad === p)
                paint(p, false);
        });
    }
    catch {
        // refused: the status line says the screen may sleep
    }
    finally {
        p.wakeLockAsking = false;
    }
    if (pad === p)
        paint(p, false);
}
function catchUp(p) {
    p.queue.retryNow();
    flush(p);
    p.loader.retry();
    void syncClock(p);
}
function flash(p, el) {
    el.classList.add("tap-flash");
    later(p, 150, () => el.classList.remove("tap-flash"));
    navigator.vibrate?.(15);
}
/** A play button: 2PT and 3PT take their kind from nbaV2Shot at the tap, never from the paint. */
function onPlay(p, event, btn, kind) {
    event.preventDefault();
    const area = btn.closest(".nba-ft-area");
    const areaSide = area?.dataset.side;
    // §11.1's gate on the swapping buttons only: Tech FT (not one of them) never waits.
    if (area && btn.classList.contains("nba-ft-swaps") && isSide(areaSide) && performance.now() - p.swappedAt[areaSide] < nba_tap_handler_1.GATE_MS) {
        navigator.vibrate?.(30);
        return;
    }
    if (btn.disabled) {
        return refuse(p, (0, nba_tap_pad_1.nbaFtButtonMode)(kind) === "tech" ? "no technical pending: tap Tech FT first" : "no free-throw trip open: tap FT ×N first");
    }
    const atMs = tapTime(event);
    const side = isSide(btn.dataset.side) ? btn.dataset.side : null;
    let recorded = kind;
    let undoes = null;
    if (kind === "shot_2" || kind === "shot_3") {
        if (!side)
            return;
        recorded = (0, nba_pad_v2_cues_1.nbaV2Shot)(p.state, side, kind === "shot_2" ? 2 : 3, shotAge(p, side, atMs)).kind;
    }
    else if (kind === "undo") {
        const parked = p.queue.parkedIds;
        const target = nbaV2UndoTarget(p.own.filter((t) => !parked.has(t.tapId)), p.sessionId);
        if (!target)
            return refuse(p, "nothing to undo in this session");
        undoes = target.tapId;
    }
    p.refusal = null;
    send(p, recorded, side, atMs, undoes ? { undoesTapId: undoes } : {});
    flash(p, btn);
    paint(p);
    void holdScreen(p);
}
function onTip(p, event, atTip) {
    event.preventDefault();
    if (p.tipShownAt === null)
        return; // hidden
    if (event.timeStamp - p.tipShownAt < exports.TIP_GATE_MS) {
        navigator.vibrate?.(30);
        return;
    }
    const allowed = (0, nba_pad_v2_cues_1.nbaTipAnswerAllowed)(p.state, atTip);
    if (!allowed.ok)
        return refuse(p, allowed.reason);
    p.refusal = null;
    send(p, "tip_confirm", null, tapTime(event), { atTip });
    paint(p);
}
/** Start is a tap, at the press; only in a synchronized session (§16a). */
function trackStart(p, event) {
    event.preventDefault();
    if (!p.sessionSynced)
        return;
    const atMs = tapTime(event);
    send(p, "track_start", null, atMs);
    p.trackingSince = atMs;
    paint(p);
}
/** Waits for the release's click after an Exit hold; never longer than this past the release. */
exports.EXIT_CLICK_GUARD_MS = 1000;
/**
 * After Exit the picker is drawn under the finger: the click ending that press is not a tap. The
 * guard outlives the pad but not the press: gone at the next press, or soon after the release when
 * no click comes (a removed target may get none).
 */
function swallowNextClick() {
    let timer;
    const off = () => {
        window.clearTimeout(timer);
        document.removeEventListener("click", swallow, true);
        document.removeEventListener("pointerdown", off, true);
        document.removeEventListener("pointerup", released, true);
    };
    const swallow = (event) => {
        event.preventDefault();
        event.stopPropagation();
        off();
    };
    const released = () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(off, exports.EXIT_CLICK_GUARD_MS);
    };
    document.addEventListener("click", swallow, true);
    document.addEventListener("pointerdown", off, true);
    document.addEventListener("pointerup", released, true);
}
function holdSpec(p, el) {
    switch (el.dataset.hold) {
        case "resume":
            return {
                inHoldState: () => p.state?.pulled === true,
                onHold: () => {
                    send(p, "resume", null, Date.now());
                    paint(p);
                },
            };
        case "exit":
            return {
                inHoldState: () => true,
                onHold: () => {
                    swallowNextClick();
                    p.onExit();
                },
            };
        case "period":
            return {
                inHoldState: () => true,
                onHold: () => {
                    // Never a guessed target: refused while the record loads, and none when the fold's period is unknown.
                    if (p.loader.state.status !== "complete")
                        return refuse(p, "record loading");
                    send(p, "period_next", null, Date.now(), { clockPeriod: (0, exports.nbaV2PeriodTarget)(p.state?.period ?? null) });
                    paint(p);
                },
            };
        case "track-stop":
            return {
                inHoldState: () => p.trackingSince !== null,
                onHold: () => {
                    send(p, "track_stop", null, Date.now());
                    p.trackingSince = null;
                    paint(p);
                },
            };
        default:
            return undefined;
    }
}
function onPointerDown(event) {
    const p = pad;
    const target = event.target;
    if (!p || !target?.closest?.(".nba-pad-em"))
        return;
    const btn = target.closest(".nba-tap-btn");
    const kind = btn?.dataset.tapKind;
    // PULL first, with nothing ahead of its send (§18.1).
    if (btn && kind === "pull" && p.state?.pulled !== true) {
        event.preventDefault();
        send(p, "pull", null, tapTime(event));
        flash(p, btn);
        paint(p);
        p.hold.press(event, null);
        return;
    }
    const holdEl = target.closest("[data-hold]");
    if (p.hold.press(event, holdEl, holdEl ? holdSpec(p, holdEl) : undefined))
        return;
    if (parkedHold.press(event))
        return;
    if (holdEl?.dataset.hold === "track-stop")
        return trackStart(p, event);
    if (holdEl)
        return event.preventDefault(); // a press on a hold that is not held does nothing
    const tip = target.closest(".nba-em-tip-btn");
    if (tip)
        return onTip(p, event, tip.dataset.tip === "yes");
    if (btn && isV2Kind(kind) && kind !== "pull")
        onPlay(p, event, btn, kind);
}
function onPointerMove(event) {
    pad?.hold.move(event);
}
function onPointerUp(event) {
    const p = pad;
    if (!p)
        return;
    p.hold.release(event);
    if (!parkedHold.release(event))
        return;
    void (0, parked_hold_1.copyParked)(p.queue).then(({ ok, text }) => {
        if (pad !== p)
            return;
        p.refusal = { text, until: Date.now() + REFUSAL_MS, ok };
        paint(p, false);
    });
}
function onPointerCancel(event) {
    pad?.hold.cancel(event);
    parkedHold.cancel(event);
}
function onClick(event) {
    pad?.hold.swallowClick(event);
}
function onOnline() {
    if (pad)
        catchUp(pad);
}
function onVisibility() {
    if (!pad || document.visibilityState !== "visible")
        return;
    catchUp(pad);
    void holdScreen(pad);
}
function listen() {
    const root = body();
    const on = [
        [root, "pointerdown", onPointerDown, false],
        [document, "pointermove", onPointerMove, false],
        [document, "pointerup", onPointerUp, false],
        [document, "pointercancel", onPointerCancel, false],
        [document, "click", onClick, true],
        [window, "online", onOnline, false],
        [document, "visibilitychange", onVisibility, false],
    ];
    for (const [t, type, fn, capture] of on)
        t?.addEventListener(type, fn, capture);
    return () => {
        for (const [t, type, fn, capture] of on)
            t?.removeEventListener(type, fn, capture);
    };
}
function openNbaTapPadV2(gameId, teams, onExit, opts = {}) {
    closeNbaTapPadV2();
    const timers = new Set();
    (0, tap_queue_1.pruneTapStorage)(`tap-pad:nba:${gameId}`);
    const p = {
        gameId,
        teams,
        onExit,
        espnPreselect: opts.espnPreselect ?? null,
        queue: tap_queue_1.TapQueue.shared(`tap-pad:nba:${gameId}`, (taps) => (0, taps_1.postTaps)("nba", gameId, taps), tap_queue_1.browserTapStorage, Date.now, SEND_TIMEOUT_MS, { persistAcknowledged: true }),
        loader: new nba_record_1.NbaRecordLoader((afterSeq) => withDeadline((0, taps_1.fetchAllTaps)("nba", gameId, afterSeq), exports.RECORD_TIMEOUT_MS)),
        unsubscribe: () => undefined,
        sessionId: "",
        sessionSynced: false,
        own: [],
        ownIds: new Set(),
        clock: null,
        syncRun: 0,
        refusal: null,
        timers,
        hold: new hold_gesture_1.HoldGesture({ timers, isCurrent: () => pad === p }),
        ftMode: { home: "trip", away: "trip" },
        swappedAt: { home: Number.NEGATIVE_INFINITY, away: Number.NEGATIVE_INFINITY },
        wakeLock: null,
        wakeLockAsking: false,
        trackingSince: null,
        input: [],
        state: null,
        shots: { home: null, away: null },
        tipShownAt: null,
        off: () => undefined,
    };
    pad = p;
    const root = body();
    if (root)
        root.innerHTML = (0, nba_tap_pad_v2_1.renderNbaTapPadV2)(teams, `${teams.away} @ ${teams.home}`);
    p.off = listen();
    startSession(p); // unsynchronized: the first sync starts a synchronized one
    p.unsubscribe = p.loader.subscribe(() => paint(p));
    p.loader.start();
    paint(p);
    void syncClock(p);
    for (const [fn, ms] of [
        [() => { flush(p); p.loader.retry(); }, FLUSH_MS],
        [() => void syncClock(p), SYNC_MS],
        [() => paint(p, false), 1000],
        [() => { if (document.visibilityState === "visible" && p.loader.state.status === "complete")
                p.loader.refresh(); }, exports.RECORD_REFRESH_MS],
    ])
        timers.add(window.setInterval(fn, ms));
    void holdScreen(p);
}
function closeNbaTapPadV2() {
    const p = pad;
    if (!p)
        return;
    pad = null;
    for (const t of p.timers) {
        window.clearInterval(t);
        window.clearTimeout(t);
    }
    p.timers.clear();
    p.hold.reset();
    parkedHold.reset();
    p.loader.stop();
    p.unsubscribe();
    p.off();
    void p.queue.flush(); // last attempt; anything left stays in storage
    void p.wakeLock?.release();
}
